import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import FormatCurrency from 'components/Common/FormatCurrency'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import CruisesDiscountTsAndCsModal from 'components/Cruises/CruisesDiscountTsAndCsModal'
import ModalContext from 'contexts/ModalContext'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'LuxKit/Button/TextButton'
import type { CruiseOfferView } from 'hooks/Cruise/useCruiseOfferView'
import CruisePriceFromLabel from 'components/Cruises/Common/CruisePriceFromLabel'
import CruisePriceSaveUpCaption from 'components/Cruises/Common/CruisePriceSaveUpCaption'

interface Props {
  view: CruiseOfferView
  isStandalone?: boolean
  cabinCategoryFromFilter?: string
}

function CruiseTilePrices(props: Props) {
  const intl = useIntl()
  const {
    view,
    isStandalone,
    cabinCategoryFromFilter,
  } = props
  const {
    lowestPrice,
    hasOnlySuitesAvailable,
    discountPills,
  } = view
  const promotionalPrice = lowestPrice.promotionalPrice
  const showModal = useContext(ModalContext)
  const openDiscountTermsAndConditionsModal = useCallback(() => {
    showModal(<CruisesDiscountTsAndCsModal />)
  }, [showModal])

  return (
    <Group gap={8} direction="vertical" fullWidth noDisplayWhenEmpty>
      <Group
        fullWidth
        gap={4}
        direction="horizontal"
        tabletDirection="vertical"
        horizontalAlign="space-between"
        tabletHorizontalAlign="start"
        verticalAlign="end"
        noDisplayWhenEmpty
      >
        {(isStandalone || cabinCategoryFromFilter || hasOnlySuitesAvailable) && <>
          <div>
            {promotionalPrice && <>
              <PriceRowPriceCaption><CruisePriceFromLabel /></PriceRowPriceCaption>
              <PriceRowPrice size="L" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />
              {promotionalPrice.perPersonPrice !== undefined && <Caption variant="medium" weight="bold" colour="success-dark">
                <FormattedMessage
                  id="UDli10X2x9NsPnjIDvB0"
                  defaultMessage="{price}/person"
                  description="Per-person equivalent shown under a fixed cruise promotional total price"
                  values={{ price: <FormatCurrency value={promotionalPrice.perPersonPrice} /> }}
                />
              </Caption>}
            </>}
            {!promotionalPrice && <>
              <PriceRowPriceCaption><CruisePriceFromLabel /></PriceRowPriceCaption>
              <Group direction="horizontal" gap={4} verticalAlign="end">
                <PriceRowPrice
                  size="L"
                  price={lowestPrice.primaryPrice.total}
                  saleUnit={lowestPrice.primaryPrice.type}
                />
              </Group>
              {lowestPrice.secondaryPrice && <PriceRowCaption>
                <FormattedMessage
                  id="asuxn0hT13GBAcMsCSyh"
                  defaultMessage="{price} /{priceType} · {twinShareLabel}"
                  description="Cruise secondary price with sale unit and twin share label"
                  values={{
                    price: <FormatCurrency value={lowestPrice.secondaryPrice.total} />,
                    priceType: lowestPrice.secondaryPrice.type,
                    twinShareLabel: intl.formatMessage({
                      id: 'wG9RjdIdO6BaA70p0bC8',
                      defaultMessage: 'Twin Share',
                      description: 'Label indicating twin share pricing for cruise cabin with secondary price',
                    }),
                  }}
                />
              </PriceRowCaption>}
            </>}
            {!lowestPrice.secondaryPrice && promotionalPrice && !!discountPills && discountPills.discountPrice > 0 && <CruisePriceSaveUpCaption
              size="M"
              discountAmount={discountPills.discountPrice}
              discountPercentage={discountPills.discountPercentage}
              onInfoIconClick={openDiscountTermsAndConditionsModal}
            />}
            {!lowestPrice.secondaryPrice && !promotionalPrice && discountPills?.price && discountPills.discountPercentage && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={discountPills.price}
              discountPercentage={discountPills.discountPercentage}
              onInfoIconClick={openDiscountTermsAndConditionsModal}
            />}
            {!promotionalPrice && !lowestPrice.secondaryPrice && <PriceRowCaption>{intl.formatMessage({
              id: 'rzR7jiFXMH8zoYzMhw9L',
              defaultMessage: 'Twin Share',
              description: 'Label indicating twin share pricing for cruise cabin',
            })}
            </PriceRowCaption>}
            <PriceRowTaxesAndFees />
          </div>
          <CSSBreakpoint max="mobile">
            <TextButton kind="primary">
              {intl.formatMessage({
                id: 'bzFX_M0WawCAucgnuXxh',
                defaultMessage: 'View',
                description: 'Button text to view cruise offer details, shown on mobile breakpoint only',
              })}
            </TextButton>
          </CSSBreakpoint>
        </>}
      </Group>
    </Group>
  )
}

export default CruiseTilePrices
